import { BdExportAsFileConverter } from '../export.converter';
import type { WorkBook, WorkSheet } from 'xlsx';
// Type-only reference to the `xlsx` module shape — never a runtime import.
type XlsxModule = typeof import('xlsx');
import {
  BD_NUMBER_FORMATTER_OPTIONS_DEFAULT,
  BdExportAsFileDataRow,
  BdExportAsFileDataRowItem,
  BdExportAsFileDataSheet,
  BdExportAsFileExcelHelper,
} from '@bindoc-projects/util-domain-lib';
import DateTimeFormatOptions = Intl.DateTimeFormatOptions;
import NumberFormatOptions = Intl.NumberFormatOptions;

/** SheetJS's hard limit for a sheet name (`check_ws_name`). */
const MAX_SHEET_NAME_LENGTH = 31;

export class BdExportAsFileExcelConverter extends BdExportAsFileConverter {
  protected defaultDateTimeFormat: string;

  constructor(
    private _exportData: Promise<
      BdExportAsFileDataSheet | BdExportAsFileDataSheet[]
    >,
    locale: string,
    filenameWithoutSuffix: string,
  ) {
    super(locale, `${filenameWithoutSuffix}.xlsx`);

    this.defaultDateTimeFormat = Intl.DateTimeFormat(locale)
      .format(new Date(1999, 11, 31))
      .replace('1999', 'yyyy')
      .replace('12', 'mm')
      .replace('31', 'dd');
  }

  async process(): Promise<{ result?: Blob; errors?: string[] }> {
    try {
      const exportData = await this._exportData;
      // Loaded lazily: SheetJS is a 329 KB bundle only needed once an export
      // actually runs, not on every app's eager startup path.
      const xlsx: XlsxModule = await import('xlsx');
      const workbook = xlsx.utils.book_new();

      if (Array.isArray(exportData)) {
        for (const sheet of exportData) {
          this._addSheet(xlsx, workbook, sheet);
        }
      } else {
        this._addSheet(xlsx, workbook, exportData);
      }

      const wbout = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      return { result: blob };
    } catch (err) {
      return {
        errors: [err instanceof Error ? err.message : 'Unknown error'],
      };
    }
  }

  private _addSheet(
    xlsx: XlsxModule,
    workbook: WorkBook,
    sheetData: BdExportAsFileDataSheet,
  ): void {
    const aoa = this._toAoa(sheetData.data, sheetData.fields);
    const worksheet = xlsx.utils.aoa_to_sheet(aoa);

    this._applyHeaderStyle(xlsx, worksheet, sheetData.fields.length);
    this._applyNumberFormats(
      xlsx,
      worksheet,
      sheetData.data,
      sheetData.fields.length,
    );

    const baseSheetName =
      BdExportAsFileExcelHelper.sanitizeSheetName(sheetData.sheetName) ?? '';
    const sheetName = this._uniqueSheetName(workbook, baseSheetName);
    xlsx.utils.book_append_sheet(workbook, worksheet, sheetName);
  }

  /**
   * Two tiles/layers can sanitize to the same (possibly 31-char-truncated)
   * sheet name — `book_append_sheet` throws "Duplicate Sheet Name" in that
   * case (BDA-5941, map-tile export with same-prefix layer names). Append a
   * counter suffix, still respecting the 31-char sheet-name limit.
   */
  private _uniqueSheetName(workbook: WorkBook, baseName: string): string {
    if (!workbook.SheetNames.includes(baseName)) {
      return baseName;
    }

    let candidate: string;
    let suffixIndex = 2;
    do {
      const suffix = ` (${suffixIndex})`;
      candidate = baseName.substring(0, MAX_SHEET_NAME_LENGTH - suffix.length) + suffix;
      suffixIndex++;
    } while (workbook.SheetNames.includes(candidate));

    return candidate;
  }

  private _toAoa(
    rows: BdExportAsFileDataRow[],
    fields: string[],
  ): (string | number | boolean | Date | null)[][] {
    const header: (string | number | boolean | Date | null)[] = [...fields];
    const dataRows = rows.map((row) =>
      row.map((item) => this._cellValue(item)),
    );
    return [header, ...dataRows];
  }

  private _cellValue(
    item: BdExportAsFileDataRowItem,
  ): string | number | boolean | Date | null {
    if (item.value === null || item.value === undefined) {
      return null;
    }
    return item.value;
  }

  private _applyHeaderStyle(
    xlsx: XlsxModule,
    worksheet: WorkSheet,
    colCount: number,
  ): void {
    for (let c = 0; c < colCount; c++) {
      const addr = xlsx.utils.encode_cell({ r: 0, c });
      const cell = worksheet[addr];
      if (cell) {
        cell.s = { font: { bold: true } };
      }
    }
  }

  private _applyNumberFormats(
    xlsx: XlsxModule,
    worksheet: WorkSheet,
    rows: BdExportAsFileDataRow[],
    colCount: number,
  ): void {
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      for (let c = 0; c < Math.min(row.length, colCount); c++) {
        const item = row[c];
        if (item.type === 'number' && item.value != null) {
          const addr = xlsx.utils.encode_cell({ r: r + 1, c });
          const cell = worksheet[addr];
          if (cell) {
            const fmt = this._numberFormat(item.value, item.format);
            if (fmt) {
              cell.z = fmt;
            }
          }
        } else if (item.type === 'Date' && item.value != null) {
          const addr = xlsx.utils.encode_cell({ r: r + 1, c });
          const cell = worksheet[addr];
          if (cell) {
            cell.z =
              typeof item.format === 'string'
                ? item.format
                : this.defaultDateTimeFormat;
          }
        }
      }
    }
  }

  private _numberFormat(
    value: number | string | boolean | Date | null,
    format?: string | NumberFormatOptions | DateTimeFormatOptions,
  ): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (!format) {
      return this._defaultNumberFormat(value);
    }

    if (typeof format === 'string') {
      return format;
    }

    let fractionPattern: string | undefined;
    if (BdExportAsFileExcelConverter._instanceOfNumberFormatOptions(format)) {
      fractionPattern = this._fractionPattern(format as NumberFormatOptions);
    }
    return this._defaultNumberFormat(value, fractionPattern);
  }

  private _defaultNumberFormat(
    value: number | string | boolean | Date | null,
    fractionPattern?: string | undefined,
  ): string {
    if (Number(value) === value && value % 1 === 0) {
      return fractionPattern ? `0.${fractionPattern}` : `0`;
    } else {
      fractionPattern =
        fractionPattern ||
        this._fractionPattern(BD_NUMBER_FORMATTER_OPTIONS_DEFAULT);
      return `0.${fractionPattern}`;
    }
  }

  private _fractionPattern(options: NumberFormatOptions): string | undefined {
    const minDigits = options.minimumFractionDigits || 0;
    const maxDigits = options.maximumFractionDigits || minDigits;
    return '0'.repeat(maxDigits);
  }
}
